import {
  Card, CardHeader, Typography, Stack,
} from '@mui/material';
// utils
import { useMemo } from 'react';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../hooks/useDashboard';

type ProgressItemProps = {
  progress: any;
};

const BorderLinearProgress = styled(LinearProgress)(({ theme, color }) => ({
  height: 6,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: color, // Fallback to default if no color is passed
    ...theme.applyStyles('dark', {
      backgroundColor: color, // Apply passed color in dark mode
    }),
  },
}));

function ProgressItem({ progress }: ProgressItemProps) {
  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {progress.title}
        </Typography>
        <Typography variant="subtitle2">{progress.value}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          &nbsp;(
          {progress.formattedValue || 0}
          %
          )
        </Typography>
      </Stack>

      <BorderLinearProgress
        variant="determinate"
        value={progress.formattedValue || 0}
        color={progress.color}
      />
    </Stack>
  );
}

const DashboardTransactions = () => {
  const { getTransactionsItems } = useDashboard();
  const { t } = useTranslation('dashboard');

  const transactionItems = useMemo(() => {
    const totalValue = getTransactionsItems.reduce((sum, item) => {
      const numericValue = parseFloat(item.value.toString().replace(/[$,]/g, ''));
      return sum + (Number.isNaN(numericValue) ? 0 : numericValue);
    }, 0);

    return getTransactionsItems.map((item) => {
      const numericValue = parseFloat(item.value.toString().replace(/[$,]/g, ''));
      const validNumericValue = Number.isNaN(numericValue) ? 0 : numericValue;

      const percentageValue = totalValue > 0 ? (validNumericValue / totalValue) * 100 : 0;

      return {
        ...item,
        formattedValue: parseFloat(percentageValue.toFixed(2)),
      };
    });
  }, [getTransactionsItems]);

  return (
    <Card sx={{
      mt: 2,
    }}
    >
      <CardHeader title={t('transactionsOverview')} />
      <Stack spacing={4} sx={{ p: 3 }}>
        {transactionItems.map((transaction) => (
          <ProgressItem key={transaction.id} progress={transaction} />
        ))}
      </Stack>
    </Card>
  );
};

export {
  DashboardTransactions,
};
